export type Finding = { id: string; severity: string; type?: string; title: string; detail: string; loopId?: string };
export type PresentedFinding = Finding & { href: string; grouped?: number };

const severityRank: Record<string, number> = { high: 0, medium: 1, low: 2 };

function isVisibilityFinding(finding: Finding): boolean {
  return finding.type === "visibility" || /has not reported a run yet$/u.test(finding.title);
}

/**
 * Presents deterministic diagnostics for the Pulse page.
 * Individual "has not reported a run yet" findings are collapsed into one summary row so a freshly
 * claimed catalog does not read as eight separate problems; the row points at run-reporting activation.
 */
export function groupFindings(findings: Finding[], hrefs: { activationHref: string; loopsHref: string }): PresentedFinding[] {
  const visibility = findings.filter(isVisibilityFinding);
  const others: PresentedFinding[] = findings
    .filter((finding) => !isVisibilityFinding(finding))
    .map((finding, index) => ({ finding, index }))
    .sort((a, b) => (severityRank[a.finding.severity] ?? 3) - (severityRank[b.finding.severity] ?? 3) || a.index - b.index)
    .map(({ finding }) => ({ ...finding, href: finding.loopId ? `/app/loops/${finding.loopId}` : hrefs.loopsHref }));
  if (visibility.length === 0) return others;
  if (visibility.length === 1) return [...others, { ...visibility[0], href: hrefs.activationHref }];
  return [
    ...others,
    {
      id: "visibility:group",
      severity: "low",
      type: "visibility",
      title: `${visibility.length} loops have not reported a run yet`,
      detail: "They are cataloged but not reporting. Turn on run reporting for the loops you want outcomes from.",
      href: hrefs.activationHref,
      grouped: visibility.length,
    },
  ];
}
